// The keyboard state of the hashtag suggestion list in the add field. No Vue in here, so the rules are easy to test:
// the list opens with nothing selected, Down starts at the top and Up at the bottom entry, Escape closes it and always
// drops the selection, typing reopens it and keeps a selection that is still among the shown entries (and drops one that
// is not), Tab reopens a closed list. The selection is a tag name, not an index, so it follows a list that changes.

export interface NavState {
  closed: boolean // (closed by Escape, until the next input or Tab)
  selected: string | null
}

export const initialNav: NavState = { closed: false, selected: null }

export function selectedIndex(state: NavState, names: string[]): number {
  return state.selected === null ? -1 : names.indexOf(state.selected)
}

// One step down (1) or up (-1) through `names`, wrapping around; from nothing, Down goes to the first entry and Up to the
// last one.
export function moveSelection(state: NavState, names: string[], step: 1 | -1): NavState {
  if (!names.length) return state
  const current = selectedIndex(state, names)
  const next =
    current < 0 ? (step > 0 ? 0 : names.length - 1) : (current + step + names.length) % names.length
  return { ...state, selected: names[next] }
}

export function escape(state: NavState): NavState {
  return { ...state, closed: true, selected: null }
}

// The text changed: the list is open again, whatever is selected stays until `reconcile` checks it against the new list.
export function typed(state: NavState): NavState {
  return { closed: false, selected: state.selected }
}

// A fresh search, as after Tab on a closed list.
export function reopen(): NavState {
  return { closed: false, selected: null }
}

// A selection that is no longer among the shown entries (no longer matches, or pushed out of the limited list) is dropped.
export function reconcile(state: NavState, names: string[]): NavState {
  return state.selected !== null && !names.includes(state.selected)
    ? { ...state, selected: null }
    : state
}

// What Tab or Enter take: the selected name, if it is (still) in the list.
export function accepted(state: NavState, names: string[]): string | null {
  return selectedIndex(state, names) >= 0 ? state.selected : null
}
